import React, { useCallback, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import PropTypes from 'prop-types'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import SydButton from '../../../../../commonDesign/Button'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import { decipherError } from '../../../../../../utils/decipherError'
import { dataCatalog } from '../../../../../../api/coreData'
import Editor from '../../../../../organisms/WYSIWYGEditor'
import FlexRow from '../../../../../molecules/FlexRow'

const useStyles = makeStyles((theme) => ({
  editorContainer: {
    border: 'var(--border-neutral-thin)',
    '& .editor-input': {
      boxShadow: '0 4px 2px -4px inset',
      overflow: 'auto',
      maxHeight: '50vh'
    }
  },
  header: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius
  },
  tableContainer: {
    marginBottom: theme.spacing(2),
    maxHeight: '60vh',
    overflow: 'auto'
  },
  summary: {
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.info.contrastText
  }
}))

const useSubmitter = (dataSource, datapoint, onComplete) => {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { mutateAsync: modifyDatasourceDescription } = dataCatalog.useModifyDatapointDescription()

  const onSubmit = useCallback(async (formData) => {
    const command = {
      datasourceId: dataSource.catalogDatasourceId,
      accessor: datapoint.accessor,
      description: formData.description
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await modifyDatasourceDescription(command)
      if (result?.statusCode === 500) {
        setError('Something unexpected happened while updating the datasource description')
        return
      }

      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [dataSource, datapoint, onComplete, modifyDatasourceDescription])

  return {
    onSubmit,
    processing,
    error
  }
}

function DescribeDatapointForm ({ dataSource, datapoint, onCancel, onComplete }) {
  const classes = useStyles()
  const [description, setDescription] = useState(datapoint.description || '')
  const { onSubmit, processing, error } = useSubmitter(dataSource, datapoint, onComplete)

  if (!dataSource) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Alert severity='error'>No datapoint provided</Alert>
        </Grid>
      </Grid>
    )
  }

  return (
    <div className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FlexRow className='gap-md'>
            <span>{datapoint.displayName}</span>
            <span style={{ fontWeight: 'var(--font-weight-light)' }}>({datapoint.accessor})</span>
          </FlexRow>
        </Grid>
        <Grid item xs={12}>
          <Editor
            className={classes.editorContainer}
            defaultValue={description}
            placeholder='Enter a description'
            isMarkdown
            onChange={setDescription}
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12}>
          <SydModalActions>
            {error && <div className='__error'>{error}</div>}
            <SydButton
              disabled={processing}
              variant='ghost'
              size='md'
              onClick={onCancel}
            >
              Cancel
            </SydButton>
            <SydButton
              processing={processing}
              variant='primary'
              size='md'
              onClick={() => onSubmit({ description })}
            >
              Save Changes
            </SydButton>
          </SydModalActions>
        </Grid>
      </Grid>
    </div>
  )
}

DescribeDatapointForm.propTypes = {
  dataSource: PropTypes.shape({
    catalogDatasourceId: PropTypes.number.isRequired
  }).isRequired,
  datapoint: PropTypes.shape({
    catalogDatapointId: PropTypes.number,
    accessor: PropTypes.string,
    displayName: PropTypes.string,
    description: PropTypes.string
  }),
  onComplete: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default DescribeDatapointForm
